/**
 * ToastMessages - Sequence of toast notifications that fly in from the bottom and stack upward.
 * Layout: icon left, description (small/gray) upper right, main text (large/white) lower right.
 */

import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { HeartIcon } from "@/components/icons/HeartIcon";
import CashIcon from "@/components/icons/CashIcon";
import BankIcon from "@/components/icons/BankIcon";
import DecorationIcon from "@/components/icons/DecorationIcon";
import TrophyIcon from "@/components/icons/TrophyIcon";
import type { ToastMessage } from "@/types/state";

type ToastMessagesProps = {
    toasts: ToastMessage[];
    onComplete: () => void;
};

const TOAST_STAGGER_MS = 850;
const HOLD_AFTER_LAST_MS = 1200;
const ICON_SIZE = 24;
const SLIDE_UP_DISTANCE = 825;
const SLIDE_UP_DURATION = 0.45;
const FADE_OUT_DELAY = 0.25;
const FADE_OUT_DURATION = 0.15;


const toastVariants = {
    initial: { opacity: 0, y: 80, scale: 0.9 },
    animate: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.35, ease: "easeOut" },
    },
    exit: {
        opacity: 0,
        y: -SLIDE_UP_DISTANCE,
        transition: {
            y: { duration: SLIDE_UP_DURATION, ease: "easeIn" },
            opacity: { duration: FADE_OUT_DURATION, delay: FADE_OUT_DELAY, ease: "easeIn" },
        },
    },
} as const;

type ToastMessageItemProps = {
    icon: ReactNode;
    description: string;
    main: ReactNode;
    toastType?: string;
};

const ToastMessageItem = ({ icon, description, main, toastType }: ToastMessageItemProps) => (
    <>
        <div className="flex-shrink-0 text-white" data-toast-type={toastType} data-toast-role="icon">
            {icon}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span
                className="text-xs text-white/65 leading-tight"
                data-toast-type={toastType}
                data-toast-role="description"
            >
                {description}
            </span>
            <span
                className="text-sm font-semibold text-white leading-tight"
                data-toast-type={toastType}
                data-toast-role="main"
            >
                {main}
            </span>
        </div>
    </>
);

const getToastItemProps = (toast: ToastMessage): ToastMessageItemProps & { toastType: string } => {
    switch (toast.type) {
        case "cash":
            return {
                icon: <CashIcon width={ICON_SIZE} height={ICON_SIZE} />,
                description: "Cash",
                main: (
                    <span className={toast.change >= 0 ? "text-success-500" : "text-danger-400"}>
                        {toast.change > 0 ? "+" : ""}
                        {toast.change} €
                    </span>
                ),
                toastType: "cash",
            };
        case "depot":
            return {
                icon: <BankIcon width={ICON_SIZE} height={ICON_SIZE} />,
                description: "Depot",
                main: (
                    <span className={toast.change >= 0 ? "text-success-500" : "text-danger-400"}>
                        {toast.change > 0 ? "+" : ""}
                        {toast.change} €
                    </span>
                ),
                toastType: "depot",
            };
        case "earnings":
            return {
                icon: <BankIcon width={ICON_SIZE} height={ICON_SIZE} />,
                description: "Rendite",
                main: (
                    <span className={toast.change >= 0 ? "text-success-500" : "text-danger-400"}>
                        {toast.change > 0 ? "+" : ""}
                        {toast.change} €
                    </span>
                ),
                toastType: "depot",
            };
        case "happiness":
            return {
                icon: <HeartIcon width={ICON_SIZE} height={ICON_SIZE} className="fill-danger-500" />,
                description: "Happiness",
                main: `${toast.change > 0 ? "+" : ""}${toast.change}`,
                toastType: "happiness",
            };
        case "achievement":
            return {
                icon: (
                    <span className="text-[#FFB700]">
                        <TrophyIcon width={ICON_SIZE} height={ICON_SIZE} />
                    </span>
                ),
                description: "Trophäe",
                main: toast.achievement?.name ?? "Erhalten!",
                toastType: "achievement",
            };
        case "item":
            return {
                icon: <DecorationIcon width={ICON_SIZE} height={ICON_SIZE} />,
                description: "Item",
                main: toast.item?.description ?? "Erhalten!",
                toastType: "item",
            };
        default:
            return {
                icon: <HeartIcon width={ICON_SIZE} height={ICON_SIZE} />,
                description: "Toast",
                main: "",
                toastType: "unknown",
            };
    }
};

export const ToastMessages = ({ toasts, onComplete }: ToastMessagesProps) => {
    const [visibleCount, setVisibleCount] = useState(0);
    const [dismissing, setDismissing] = useState(false);

    useEffect(() => {
        if (visibleCount >= toasts.length) return;
        const delay = visibleCount === 0 ? 0 : TOAST_STAGGER_MS;
        const timer = setTimeout(() => setVisibleCount((prev) => prev + 1), delay);
        return () => clearTimeout(timer);
    }, [visibleCount, toasts.length]);

    useEffect(() => {
        if (visibleCount < toasts.length || dismissing) return;
        const timer = setTimeout(() => setDismissing(true), HOLD_AFTER_LAST_MS);
        return () => clearTimeout(timer);
    }, [visibleCount, toasts.length, dismissing]);

    const handleExitComplete = useCallback(() => {
        if (dismissing) onComplete();
    }, [dismissing, onComplete]);

    const visibleToasts = dismissing ? [] : toasts.slice(0, visibleCount);

    return (
        <div className="absolute inset-0 z-30 flex flex-col justify-end items-center gap-2 pb-10 pointer-events-none">
            <AnimatePresence onExitComplete={handleExitComplete}>
                {visibleToasts.map((toast, index) => {
                    const props = getToastItemProps(toast);
                    return (
                        <motion.div
                            key={`${toast.type}-${index}`}
                            variants={toastVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            layout
                            className="flex items-center gap-3 rounded-full border border-white/15 bg-black/65 px-4 py-3 shadow-[0_2px_4px_0_rgba(0,0,0,0.35)] backdrop-blur"
                        >
                            <ToastMessageItem
                                icon={props.icon}
                                description={props.description}
                                main={props.main}
                                toastType={props.toastType}
                            />
                        </motion.div>
                    );
                })}
            </AnimatePresence>
        </div>
    );
};
